import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X } from "lucide-react";

import { toolName, toolShortDescription, iconFor } from "../../marketplace";
import { useCenter } from "../../center";
import { useOnboardingEligibility } from "../application/useOnboardingEligibility";
import CenterWelcomeModal from "./CenterWelcomeModal";
import { iconForStep } from "./stepIcons";
import {
    useOnboardingFlow,
    type PickerGroup,
} from "../application/useOnboardingFlow";

const FOCUSABLE =
    'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * First-entry setup for Plus therapists: one screen of tools to tick, the
 * public-profile offer, then a confirmation. Shown once ever — completion is
 * persisted server-side, so it also covers a therapist who becomes Plus long
 * after signing up.
 *
 * This outer component is only a gate. The flow — and the catalogue queries it
 * needs — mount only for a therapist who will actually see the modal, so a
 * `basic` therapist pays nothing for a surface they can never reach.
 */
export default function OnboardingModal() {
    const eligible = useOnboardingEligibility();
    const { membership, loading } = useCenter();

    if (!eligible) return null;
    // Wait to know: offering a centre member the practice setup for a moment
    // would put a tool picker in front of somebody who picks nothing.
    if (loading) return null;
    // Inside a centre there is no practice to set up — the centre decides the
    // tools and covers the plan — so the member is told that instead.
    if (membership) return <CenterWelcomeModal membership={membership} />;
    return <OnboardingFlowView />;
}

function OnboardingFlowView() {
    const { t } = useTranslation();
    const {
        isOpen,
        step,
        stepNumber,
        stepCount,
        isFinalScreen,
        isBusy,
        hasFailed,
        catalogUnavailable,
        groups,
        selected,
        locked,
        failedTools,
        toggle,
        accept,
        decline,
        previous,
        close,
    } = useOnboardingFlow();

    const dialogRef = useRef<HTMLDivElement>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);

    const trapFocus = useCallback((event: KeyboardEvent) => {
        const focusable = Array.from(
            dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement as HTMLElement | null;

        if (event.shiftKey && (active === first || !dialogRef.current?.contains(active))) {
            event.preventDefault();
            last.focus();
        } else if (
            !event.shiftKey &&
            (active === last || !dialogRef.current?.contains(active))
        ) {
            event.preventDefault();
            first.focus();
        }
    }, []);

    // Escape dismisses, which counts as "set this up later".
    useEffect(() => {
        if (!isOpen) return;
        previouslyFocused.current = document.activeElement as HTMLElement | null;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                close();
                return;
            }
            if (event.key === "Tab") trapFocus(event);
        };

        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [isOpen, close, trapFocus]);

    // Each step replaces the buttons, so focus has to be placed again or it
    // falls back to the document body.
    useEffect(() => {
        if (!isOpen || !dialogRef.current) return;
        const first = dialogRef.current.querySelector<HTMLElement>(FOCUSABLE);
        first?.focus();
    }, [isOpen, step, isFinalScreen]);

    useEffect(() => {
        if (isOpen) return;
        previouslyFocused.current?.focus();
    }, [isOpen]);

    const StepIcon = iconForStep(step) ?? Sparkles;
    // The failure message names what did not activate, in the order they were
    // tried, since the rest of the screen did work.
    const failedNames = failedTools.map((tool) => toolName(t, tool)).join(", ");
    const isPicker = step?.kind === "picker";

    if (!isOpen || (step === null && !isFinalScreen)) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        key="onboarding-backdrop"
                        data-testid="onboarding-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
                        onClick={close}
                    />

                    <motion.div
                        key="onboarding-modal"
                        initial={{ opacity: 0, scale: 0.95, y: 16 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 16 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 pointer-events-none"
                    >
                        <div
                            ref={dialogRef}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="onboarding-title"
                            className={`bg-surface-raised rounded-3xl shadow-2xl border border-gray-200/80 dark:border-white/10 w-[calc(100vw-2rem)] p-6 sm:p-8 pointer-events-auto relative flex max-h-[calc(100dvh-2rem)] flex-col overflow-y-auto ${
                                // The picker is a catalogue; the rest are a
                                // sentence and two buttons.
                                //
                                // Bounded and scrollable, because the picker
                                // grew to the whole catalogue: taller than the
                                // screen, it was simply cut off, and what fell
                                // past the edge was the button that dismisses
                                // it. `dvh` rather than `vh` so a phone's
                                // shrinking toolbar does not hide it again.
                                isPicker ? "max-w-2xl" : "max-w-md"
                            }`}
                        >
                            <button
                                type="button"
                                onClick={close}
                                aria-label={t("onboarding.close")}
                                className="absolute top-4 right-4 text-content-tertiary hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>

                            <div className="flex flex-col items-center text-center gap-5">
                                <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-brand-500/10 text-brand-500">
                                    <StepIcon className="w-8 h-8" />
                                </div>

                                <h3
                                    id="onboarding-title"
                                    className="text-xl font-bold text-content-primary"
                                >
                                    {t("onboarding.title")}
                                </h3>

                                {step && (
                                    <>
                                        {stepCount > 1 && (
                                            <p className="text-xs uppercase tracking-wide text-content-tertiary">
                                                {t("onboarding.stepCounter", {
                                                    current: stepNumber,
                                                    total: stepCount,
                                                })}
                                            </p>
                                        )}

                                        <p className="text-content-tertiary text-sm leading-relaxed">
                                            {t(`onboarding.steps.${step.key}.question`)}
                                        </p>

                                        {isPicker && (
                                            <ToolPicker
                                                groups={groups}
                                                selected={selected}
                                                locked={locked}
                                                onToggle={toggle}
                                                disabled={isBusy}
                                            />
                                        )}

                                        {hasFailed && (
                                            <p role="alert" className="text-sm text-error-500">
                                                {t("onboarding.adoptError", {
                                                    tool: failedNames,
                                                })}
                                            </p>
                                        )}

                                        {catalogUnavailable && (
                                            <p className="text-sm text-content-tertiary">
                                                {t("onboarding.catalogUnavailable")}
                                            </p>
                                        )}

                                        <div className="w-full flex flex-col gap-2">
                                            <button
                                                type="button"
                                                onClick={accept}
                                                disabled={isBusy}
                                                className="inline-flex items-center justify-center w-full px-6 py-3 rounded-xl bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white font-semibold text-sm transition-colors shadow-brand-500/30 shadow-lg"
                                            >
                                                {hasFailed
                                                    ? t("onboarding.retry")
                                                    : t(`onboarding.steps.${step.key}.accept`)}
                                            </button>

                                            <button
                                                type="button"
                                                onClick={decline}
                                                disabled={isBusy}
                                                className="inline-flex items-center justify-center w-full px-6 py-3 rounded-xl border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 font-medium text-sm transition-colors hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50"
                                            >
                                                {t(`onboarding.steps.${step.key}.skip`)}
                                            </button>
                                        </div>
                                    </>
                                )}

                                {isFinalScreen && (
                                    <>
                                        <p className="text-content-tertiary text-sm leading-relaxed">
                                            {t("onboarding.final.message")}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={close}
                                            className="inline-flex items-center justify-center w-full px-6 py-3 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-semibold text-sm transition-colors shadow-brand-500/30 shadow-lg"
                                        >
                                            {t("onboarding.final.cta")}
                                        </button>
                                    </>
                                )}

                                <div className="flex items-center gap-4">
                                    {stepNumber > 1 && !isFinalScreen && (
                                        <button
                                            type="button"
                                            onClick={previous}
                                            disabled={isBusy}
                                            className="text-sm text-content-tertiary hover:text-gray-600 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
                                        >
                                            {t("common.previous")}
                                        </button>
                                    )}

                                    {!isFinalScreen && (
                                        <button
                                            type="button"
                                            onClick={close}
                                            className="text-sm text-content-tertiary hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                                        >
                                            {t("onboarding.later")}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

/**
 * The catalogue, as a screen a therapist sets their practice up on.
 *
 * Grouped the way the dock groups it, so the headings already mean what they
 * will mean tomorrow, and left-aligned inside a centred modal because a list
 * of choices is read down its left edge rather than from the middle out.
 */
function ToolPicker({
    groups,
    selected,
    locked,
    onToggle,
    disabled,
}: {
    groups: PickerGroup[];
    selected: ReadonlySet<string>;
    /** Paid for already: shown on, and not somebody's to switch off here. */
    locked: ReadonlySet<string>;
    onToggle: (toolId: string) => void;
    disabled: boolean;
}) {
    const { t } = useTranslation();

    return (
        <div className="flex w-full flex-col gap-5 text-left">
            {groups.map((group) => (
                <fieldset key={group.key} disabled={disabled} className="min-w-0">
                    <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-tertiary">
                        {t(group.labelKey)}
                    </legend>

                    <div className="grid gap-2 sm:grid-cols-2">
                        {group.tools.map((tool) => {
                            const Icon = iconFor(tool.icon);
                            const isOn = selected.has(tool.id);
                            const isFixed = locked.has(tool.id);
                            return (
                                <label
                                    key={tool.id}
                                    className={`flex min-w-0 items-start gap-3 rounded-2xl border p-3 transition-colors ${
                                        isFixed ? "cursor-default" : "cursor-pointer"
                                    } ${
                                        isOn
                                            ? "border-brand-500 bg-brand-50/60 dark:bg-brand-500/10"
                                            : "border-border-subtle hover:border-brand-500"
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isOn}
                                        disabled={isFixed}
                                        onChange={() => onToggle(tool.id)}
                                        className="mt-1 size-4 shrink-0 accent-[var(--color-brand-500)]"
                                    />
                                    <span className="min-w-0">
                                        <span className="flex items-center gap-2 text-sm font-semibold text-content-primary">
                                            <Icon className="h-4 w-4 text-brand-500" />
                                            {toolName(t, tool)}
                                        </span>
                                        <span className="mt-0.5 block text-xs leading-relaxed text-content-tertiary">
                                            {toolShortDescription(t, tool)}
                                        </span>
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </fieldset>
            ))}
        </div>
    );
}
